import React, { useState } from 'react';
import { TrendingUpIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const currentYear = new Date().getFullYear();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-cream py-12 px-4 text-center" data-id="element-54">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-4" data-id="element-55">
          <div className="flex items-center gap-2 text-gold font-heading text-xl font-bold" data-id="element-56">
            <TrendingUpIcon className="w-6 h-6" data-id="element-57" />
            <span data-id="element-58">Control Financiero Latina</span>
          </div>
          <p className="text-cream/60 text-sm" data-id="element-59">
            Empoderando a mamás latinas a tomar el control de su futuro.
          </p>
          <div className="w-12 h-px bg-cream/20 my-4" data-id="element-60" />
          <div className="flex items-center gap-6 text-sm" data-id="element-61">
            <button onClick={() => setActiveModal('privacy')} className="text-cream/50 hover:text-gold transition-colors underline underline-offset-2" data-id="element-62">
              Política de Privacidad
            </button>
            <span className="text-cream/20" data-id="element-63">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-cream/50 hover:text-gold transition-colors underline underline-offset-2" data-id="element-64">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-cream/40 text-xs mt-2" data-id="element-65">
            &copy; {currentYear} Control Financiero Latina. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      {/* Legal Modal */}
      <AnimatePresence data-id="element-66">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-67">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="bg-cream text-charcoal rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-id="element-68">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-charcoal/10 bg-white/50 flex-shrink-0" data-id="element-69">
                <h3 className="text-xl font-heading font-bold text-purple-dark" data-id="element-70">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-9 h-9 rounded-full bg-charcoal/5 hover:bg-charcoal/10 flex items-center justify-center transition-colors" aria-label="Cerrar" data-id="element-71">
                  <XIcon className="w-5 h-5 text-charcoal/60" data-id="element-72" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-6 overflow-y-auto text-sm leading-relaxed space-y-4 text-charcoal/80" data-id="element-73">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-74" /> : <TermsContent data-id="element-75" />}
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t border-charcoal/10 bg-white/50 flex-shrink-0" data-id="element-76">
                <button onClick={() => setActiveModal(null)} className="w-full sm:w-auto px-6 py-2.5 bg-purple text-white font-semibold rounded-full hover:bg-purple-dark transition-colors text-sm" data-id="element-77">
                  Entendido
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-charcoal/50 italic" data-id="element-78">
        Última actualización: marzo 2026
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-79">
        1. Información que Recopilamos
      </h4>
      <p data-id="element-80">
        Al realizar tu compra a través de Etsy, se recopila únicamente la
        información necesaria para procesar tu pedido: nombre, correo
        electrónico y datos de pago. Nosotros <strong data-id="element-81">no</strong> almacenamos
        información de tarjetas de crédito ni datos financieros personales.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-82">
        2. Uso de la Información
      </h4>
      <p data-id="element-83">
        Tu información se utiliza exclusivamente para: entregar el producto
        digital adquirido, enviar actualizaciones gratuitas del producto y
        brindarte soporte si lo necesitas. Nunca vendemos, alquilamos ni
        compartimos tu información personal con terceros.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-84">
        3. Plataforma de Venta
      </h4>
      <p data-id="element-85">
        Las transacciones se procesan a través de Etsy, Inc. Te recomendamos
        revisar la{' '}
        <span className="text-purple font-medium" data-id="element-86">
          política de privacidad de Etsy
        </span>{' '}
        para conocer cómo ellos manejan tus datos durante el proceso de compra.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-87">
        4. Archivos Digitales
      </h4>
      <p data-id="element-88">
        Los archivos Excel y PDF que recibes son para tu uso personal y
        familiar. Los datos que ingreses en las plantillas permanecen únicamente
        en tu dispositivo — nosotros no tenemos acceso a ellos.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-89">
        5. Cookies y Seguimiento
      </h4>
      <p data-id="element-90">
        Esta página puede utilizar cookies básicas de funcionamiento. No
        utilizamos cookies de seguimiento publicitario ni rastreadores de
        terceros.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-91">6. Tus Derechos</h4>
      <p data-id="element-92">
        Puedes solicitar la eliminación de tus datos en cualquier momento
        contactándonos a través de Etsy. Responderemos en un plazo máximo de 30
        días.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-charcoal/50 italic" data-id="element-93">
        Última actualización: marzo 2026
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-94">1. Producto Digital</h4>
      <p data-id="element-95">
        "Plantillas de Presupuesto en Excel para Madres Solteras Latinas" es un
        producto digital que incluye archivos Excel (.xlsx), PDFs imprimibles,
        copias para Google Sheets y videos guía. Al completar tu compra, recibes
        acceso inmediato y vitalicio a todos los archivos y futuras
        actualizaciones.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-96">2. Licencia de Uso</h4>
      <p data-id="element-97">
        Adquieres una licencia personal e intransferible. Puedes usar las
        plantillas para ti y tu familia. <strong data-id="element-98">No está permitido</strong>{' '}
        revender, redistribuir, compartir públicamente ni modificar los archivos
        para venta.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-99">3. Precio y Pago</h4>
      <p data-id="element-100">
        El precio del producto es de $17 USD (o su equivalente en tu moneda
        local según Etsy). El pago se procesa de forma segura a través de la
        plataforma Etsy.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-101">
        4. Política de Devoluciones
      </h4>
      <p data-id="element-102">
        Debido a la naturaleza digital del producto, las devoluciones se rigen
        por la política estándar de Etsy para productos digitales. Si
        experimentas algún problema técnico con los archivos, te ayudaremos a
        resolverlo.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-103">5. Actualizaciones</h4>
      <p data-id="element-104">
        Las actualizaciones futuras del producto están incluidas sin costo
        adicional. Recibirás notificación por Etsy cuando haya nuevas versiones
        disponibles.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-105">
        6. Descargo de Responsabilidad
      </h4>
      <p data-id="element-106">
        Las plantillas son herramientas educativas de organización financiera
        personal. No constituyen asesoría financiera, fiscal ni legal
        profesional. Los resultados pueden variar según la situación individual
        de cada persona. Los ejemplos y testimonios reflejan experiencias
        individuales y no garantizan resultados específicos.
      </p>

      <h4 className="font-bold text-charcoal text-base" data-id="element-107">
        7. Propiedad Intelectual
      </h4>
      <p data-id="element-108">
        Todo el contenido, diseño, fórmulas y materiales incluidos son propiedad
        de Control Financiero Latina y están protegidos por derechos de autor.
      </p>
    </>;
}